import { Input } from "@/components/ui/input";

/**
 * Format a raw digit string into US-standard phone display.
 * Accepts any input; strips non-digits; truncates to 10 digits.
 *
 *   ""         → ""
 *   "9"        → "9"
 *   "904"      → "904"
 *   "9045"     → "(904) 5"
 *   "9045551"  → "(904) 555-1"
 *   "9045551234" → "(904) 555-1234"
 *   "19045551234" (11+ digits) → digits after the first are used up to 10
 */
export function formatPhone(raw) {
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Return only the digits from a phone string. */
export function phoneDigits(v) {
    return String(v || "").replace(/\D/g, "").slice(0, 10);
}

/**
 * PhoneInput — auto-formats to (XXX) XXX-XXXX as the user types.
 *
 * Value round-trips as a formatted string (parent stores/reads the display value).
 * onChange fires with the formatted string. If parent needs raw digits, use `phoneDigits(value)`.
 */
export default function PhoneInput({ value, onChange, className = "", ...rest }) {
    const handle = (e) => {
        const next = formatPhone(e.target.value);
        onChange(next);
    };
    return (
        <Input
            {...rest}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder={rest.placeholder ?? "(555) 555-1234"}
            value={formatPhone(value)}
            onChange={handle}
            maxLength={14}  // "(XXX) XXX-XXXX" length
            className={className}
        />
    );
}
